import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { resolveRoute } from '@/plugin/slots'
import { Briefcase, FlaskConical, Loader2, MoreHorizontal, Plus, Sparkles, Trash2 } from 'lucide-react'
import { toast } from 'sonner'

import { PageContainer } from '@/components/ui/page-container'
import { PageHeader } from '@/components/ui/page-header'
import { PageHeaderLeading } from '@/components/navigation/page-header-leading'
import {
  PageHeaderActionPrimary,
  PageHeaderActionSecondary,
  PageHeaderActions,
} from '@/components/navigation/page-header-actions'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { EntityManageSection } from '@/components/ui/entity-manage-section'
import { ButtonDropdownChevron } from '@/components/ui/button-dropdown-chevron'
import { Card, CardAction, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import { FormField } from '@/components/ui/form-field'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { DataBenchJobPanel } from '@/components/platform/data-bench-job-panel'
import { SampleJobArchiveDialog } from '@/components/platform/sample-job-archive-dialog'
import { DataBenchTenantSweep } from '@/components/platform/platform-data-bench-removal'
import { DashboardSectionAction } from '@/components/dashboard/dashboard-section-action'
import { ListCard } from '@/components/list-card'
import { ListCardHeader } from '@/components/list-card-header'
import { PaneListEmptyState } from '@/components/layout/pane-list-empty-state'
import { PaneListSkeleton } from '@/components/layout/pane-list-skeleton'
import { PaneResponsiveCardGrid } from '@/components/layout/pane-responsive-card-grid'
import {
  useArchiveSimulationScenario,
  useCreateSimulationAlphaPack,
  useCreateSimulationDemoPack,
  useCreateSimulationScenario,
  useSimulationCapabilities,
  useSimulationJob,
  useSimulationJobStream,
  useSimulationScenarios,
  type SimulationJob,
  type SimulationScenario,
} from '@/hooks/use-platform-simulation'
import {
  dataBenchCopy,
  isProductWorldScenario,
  personCount,
  sampleJobSourceLabel,
  sampleJobStatusBadge,
} from '@/lib/platform-data-bench-copy'

type PackKind = 'demo' | 'alpha'

function scenarioIdFromJob(job: SimulationJob): string | undefined {
  return job.scenario_id || job.result?.scenario_id
}

function liveProductHrefs(scenario: SimulationScenario): {
  jobHref: string | null
  jobsHref: string | null
} {
  return {
    jobHref: scenario.opportunity_id
      ? resolveRoute('opportunity.manage', { id: scenario.opportunity_id })
      : null,
    jobsHref:
      (scenario.organisation_slug
        ? resolveRoute('organisation.opportunities', { slug: scenario.organisation_slug })
        : null) ?? resolveRoute('opportunities.manage'),
  }
}

function LiveProductReady({
  scenario,
  craftEnabled,
  onReplace,
}: {
  scenario: SimulationScenario
  craftEnabled: boolean
  onReplace: () => void
}) {
  const { jobHref, jobsHref } = liveProductHrefs(scenario)
  const liveHref = jobHref || jobsHref
  const org = scenario.organisation_name
  return (
    <>
      <p className="text-sm text-muted-foreground">
        Ready{org ? ` for ${org}` : ''}. {personCount(scenario)} people are in the live product.
      </p>
      <div className="flex flex-wrap gap-2">
        {liveHref ? (
          <Button type="button" size="sm" asChild>
            <Link to={liveHref}>{jobHref ? 'Open a published job' : 'Open published jobs'}</Link>
          </Button>
        ) : null}
        <PageHeaderActionSecondary type="button" onClick={onReplace} disabled={!craftEnabled}>
          Replace
        </PageHeaderActionSecondary>
      </div>
    </>
  )
}

function PackDialogFooter({
  onCancel,
  cancelDisabled,
  onConfirm,
  confirmDisabled,
  confirmBusy,
  confirmLabel,
}: {
  onCancel: () => void
  cancelDisabled?: boolean
  onConfirm: () => void
  confirmDisabled?: boolean
  confirmBusy?: boolean
  confirmLabel: string
}) {
  return (
    <DialogFooter>
      <Button type="button" variant="outline" onClick={onCancel} disabled={cancelDisabled}>
        Cancel
      </Button>
      <Button type="button" disabled={confirmDisabled} onClick={onConfirm}>
        {confirmBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
        {confirmLabel}
      </Button>
    </DialogFooter>
  )
}

export function DataBenchHubPage() {
  const navigate = useNavigate()
  const { data: capabilities, isLoading: capsLoading } = useSimulationCapabilities()
  const { data: scenarios, isLoading, error, refetch } = useSimulationScenarios()
  const createScenario = useCreateSimulationScenario()
  const createDemoPack = useCreateSimulationDemoPack()
  const createAlphaPack = useCreateSimulationAlphaPack()
  const archiveScenario = useArchiveSimulationScenario()

  const [createOpen, setCreateOpen] = useState(false)
  const [archiveTarget, setArchiveTarget] = useState<SimulationScenario | null>(null)
  const [demoOpen, setDemoOpen] = useState(false)
  const [alphaOpen, setAlphaOpen] = useState(false)
  const [alphaReplace, setAlphaReplace] = useState(false)
  const [name, setName] = useState('Sample job')
  const [jobTitle, setJobTitle] = useState('')
  const [packKind, setPackKind] = useState<PackKind | null>(null)
  const [packJobId, setPackJobId] = useState<string | undefined>()

  const packJobQuery = useSimulationJob(packJobId, Boolean(packJobId))
  useSimulationJobStream(packJobId)
  const packJob = packJobQuery.data
  const packBusy = Boolean(packJobId)

  const packs = capabilities?.packs ?? []
  const pack = packs[0]
  const copy = dataBenchCopy(pack)
  const craftEnabled = capabilities?.craft_enabled !== false
  const hasPack = packs.length > 0
  const demoEnabled = Boolean(capabilities?.demo_pack_enabled && copy.supportsDemo)
  const alphaEnabled = Boolean(capabilities?.alpha_pack_enabled && copy.isEmployment)

  useEffect(() => {
    if (!packJob || !packKind) return
    if (packJob.status === 'completed') {
      toast.success(
        packKind === 'alpha' ? 'Jobs and people are in the live product' : 'Job and CVs generated',
      )
      const scenarioId = scenarioIdFromJob(packJob)
      setPackJobId(undefined)
      setPackKind(null)
      setDemoOpen(false)
      setAlphaOpen(false)
      setAlphaReplace(false)
      if (scenarioId && packKind !== 'alpha') navigate(`/platform/simulation/${scenarioId}`)
    } else if (packJob.status === 'failed') {
      toast.error(
        packJob.error ||
          (packKind === 'alpha'
            ? 'Could not publish jobs and people'
            : 'Could not generate a job and CVs'),
      )
      setPackJobId(undefined)
      setPackKind(null)
    }
  }, [packJob, packKind, navigate])

  async function handleCreate() {
    if (!name.trim()) {
      toast.error('Name this sample job')
      return
    }
    try {
      const created = await createScenario.mutateAsync({
        name: name.trim(),
        methodology_id: pack?.methodology_id || 'employment_match',
        job_title: jobTitle.trim() || undefined,
        synthetic_profile_count: 0,
      })
      toast.success(`Created ${created.name}`)
      setCreateOpen(false)
      navigate(`/platform/simulation/${created.id}`)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not create run')
    }
  }

  async function handleGenerate() {
    try {
      const job = await createDemoPack.mutateAsync({
        name: copy.isEmployment ? 'Generated job' : 'Generated health sample',
        methodology_id: pack?.methodology_id,
        async_job: true,
      })
      if (job.id && job.id !== 'sync') {
        setPackKind('demo')
        setPackJobId(job.id)
        toast.message('Generating a job and CVs — this may take a few minutes')
        return
      }
      if (job.scenario_id) {
        toast.success('Job and CVs generated')
        navigate(`/platform/simulation/${job.scenario_id}`)
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not generate a job and CVs')
    }
  }

  async function handleArchive() {
    if (!archiveTarget) return
    try {
      await archiveScenario.mutateAsync(archiveTarget.id)
      toast.success(`Removed ${archiveTarget.name}`)
      setArchiveTarget(null)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not delete sample job')
    }
  }

  const sampleJobs = (scenarios ?? []).filter((scenario) => !isProductWorldScenario(scenario))
  const productWorlds = (scenarios ?? []).filter(isProductWorldScenario)
  const demoBusy = packBusy && packKind === 'demo'
  const alphaBusy = packBusy && packKind === 'alpha'
  const newSampleJobButton = (
    <PageHeaderActionPrimary
      type="button"
      onClick={() => setCreateOpen(true)}
      disabled={!craftEnabled}
      splitLead={demoEnabled}
    >
      <Plus className="h-4 w-4" aria-hidden />
      New sample job
    </PageHeaderActionPrimary>
  )
  const headerPrimary = (
    <div className="flex items-center">
      {newSampleJobButton}
      {demoEnabled ? (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <PageHeaderActionPrimary
              type="button"
              disabled={!craftEnabled}
              splitChevron
              aria-label="Other ways to start a sample job"
            >
              <ButtonDropdownChevron />
            </PageHeaderActionPrimary>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-80">
            <DropdownMenuItem className="items-start gap-3 py-2" onClick={() => setDemoOpen(true)}>
              <Sparkles className="mt-0.5" aria-hidden />
              <span className="flex flex-col gap-0.5">
                <span className="font-medium">Generate a job and CVs</span>
                <span className="text-xs text-muted-foreground">
                  {copy.isEmployment
                    ? 'A made-up job so you can check scoring quickly'
                    : `Made-up ${copy.poolNoun} so you can check scoring quickly`}
                </span>
              </span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ) : null}
    </div>
  )

  return (
    <PageContainer>
      <PageHeader
        leading={<PageHeaderLeading />}
        title={copy.pageTitle}
        description={copy.pageDescription}
        icon={<FlaskConical className="h-5 w-5" />}
        actions={
          craftEnabled && hasPack ? <PageHeaderActions primary={headerPrimary} /> : null
        }
      />

      {capsLoading || isLoading ? (
        <PaneListSkeleton ariaLabel="Loading sample jobs" />
      ) : error ? (
        <Card>
          <CardContent>
            <PaneListEmptyState
              icon={<FlaskConical aria-hidden />}
              title="Could not load sample jobs"
              description={error instanceof Error ? error.message : 'Something went wrong.'}
              action={
                <Button type="button" variant="outline" onClick={() => void refetch()}>
                  Try again
                </Button>
              }
            />
          </CardContent>
        </Card>
      ) : !hasPack ? (
        <p className="text-sm text-muted-foreground">{copy.emptyMessage}</p>
      ) : (
        <div className="space-y-6">
          {sampleJobs.length === 0 ? (
            <Card>
              <CardContent>
                <PaneListEmptyState
                  icon={<FlaskConical aria-hidden />}
                  title="No sample jobs yet"
                  description={
                    demoEnabled
                      ? 'Start one from scratch, or use the arrow to generate a job and CVs.'
                      : 'Start one from scratch to write a job and add CVs.'
                  }
                  action={
                    craftEnabled ? (
                      <DashboardSectionAction onClick={() => setCreateOpen(true)}>
                        <Plus className="h-4 w-4" aria-hidden />
                        New sample job
                      </DashboardSectionAction>
                    ) : undefined
                  }
                />
              </CardContent>
            </Card>
          ) : (
            <section
              className="space-y-4"
              aria-label={sampleJobs.length ? `Sample jobs (${sampleJobs.length})` : 'Sample jobs'}
            >
              <PaneResponsiveCardGrid columnMode="fixed-1">
                {sampleJobs.map((scenario) => {
                  const status = sampleJobStatusBadge(scenario.status)
                  const poolLabel = copy.isEmployment
                    ? `${personCount(scenario)} CVs`
                    : `${personCount(scenario)} ${copy.poolNoun}`
                  return (
                    <ListCard
                      key={scenario.id}
                      header={
                        <>
                          <ListCardHeader title={scenario.name} size="sm" />
                          <CardAction className="pointer-events-auto">
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="iconXs"
                                  aria-label={`Actions for ${scenario.name}`}
                                  onClick={(event) => event.stopPropagation()}
                                >
                                  <MoreHorizontal className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent
                                align="end"
                                onClick={(event) => event.stopPropagation()}
                              >
                                  <DropdownMenuItem
                                    className="text-destructive focus:text-destructive"
                                    onClick={() => setArchiveTarget(scenario)}
                                  >
                                    <Trash2 className="h-4 w-4" />
                                    Delete
                                  </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </CardAction>
                        </>
                      }
                      body={
                        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                          <Badge variant={status.variant}>{status.label}</Badge>
                          <span className="text-sm text-muted-foreground">
                            {`${poolLabel} · ${sampleJobSourceLabel(scenario)}`}
                          </span>
                        </div>
                      }
                      onOpen={() => navigate(`/platform/simulation/${scenario.id}`)}
                      openAriaLabel={`Open sample job ${scenario.name}`}
                    />
                  )
                })}
              </PaneResponsiveCardGrid>
            </section>
          )}

          {alphaEnabled ? (
            <Card>
              <CardHeader>
                <CardTitle>Fill Pipeline for a walkthrough</CardTitle>
                <CardDescription alwaysShow>
                  The list above stays on this page so you can score it. A hiring walkthrough needs
                  published jobs and people already waiting in the live product.
                </CardDescription>
              </CardHeader>
              <CardContent className="flex flex-col gap-3">
                {productWorlds[0] ? (
                  <LiveProductReady
                    scenario={productWorlds[0]}
                    craftEnabled={craftEnabled}
                    onReplace={() => {
                      setAlphaReplace(true)
                      setAlphaOpen(true)
                    }}
                  />
                ) : (
                  <div>
                    <PageHeaderActionSecondary
                      type="button"
                      onClick={() => {
                        setAlphaReplace(false)
                        setAlphaOpen(true)
                      }}
                      disabled={!craftEnabled}
                    >
                      <Briefcase className="h-4 w-4" aria-hidden />
                      Publish jobs and people
                    </PageHeaderActionSecondary>
                  </div>
                )}
              </CardContent>
            </Card>
          ) : null}
        </div>
      )}

      {!capsLoading && !isLoading ? (
        <>
          <EntityManageSection
            title="Simulation cleanup"
            subtitle="Count rows made by simulation tools that Platform lists still show, flag them, or delete them."
          >
            <Button asChild variant="outline">
              <Link to="/platform/simulation-cleanup">Open simulation cleanup</Link>
            </Button>
          </EntityManageSection>
          <DataBenchTenantSweep />
        </>
      ) : null}

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>New sample job</DialogTitle>
            <DialogDescription>
              {copy.isEmployment
                ? 'Starts an empty job. You write the job, add CVs, then score it.'
                : `Starts an empty ${copy.subjectNoun}. To get ${copy.poolNoun} made for you, use Generate instead.`}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <FormField label="Name this sample job" htmlFor="run-name">
              <Input id="run-name" value={name} onChange={(e) => setName(e.target.value)} />
            </FormField>
            {copy.isEmployment ? (
              <FormField label="Job title (optional)" htmlFor="run-job-title">
                <Input
                  id="run-job-title"
                  value={jobTitle}
                  onChange={(e) => setJobTitle(e.target.value)}
                  placeholder="e.g. Climate-aligned workforce partner"
                />
              </FormField>
            ) : null}
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setCreateOpen(false)}>
              Cancel
            </Button>
            <Button
              type="button"
              disabled={createScenario.isPending || !craftEnabled}
              onClick={() => void handleCreate()}
            >
              Create sample job
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={demoOpen} onOpenChange={setDemoOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Generate a job and CVs</DialogTitle>
            <DialogDescription>
              {copy.isEmployment
                ? 'Makes up a job and a set of candidates so you can score straight away. Nobody here is real.'
                : 'Makes up patients and self-declared conditions for report checks. This is not a diagnosis.'}
            </DialogDescription>
          </DialogHeader>
          {demoBusy ? (
            <DataBenchJobPanel
              kind="demo_pack"
              status={packJob?.status || 'queued'}
              progress={packJob?.progress}
              error={packJob?.error}
            />
          ) : null}
          <PackDialogFooter
            onCancel={() => setDemoOpen(false)}
            cancelDisabled={demoBusy}
            onConfirm={() => void handleGenerate()}
            confirmDisabled={createDemoPack.isPending || demoBusy}
            confirmBusy={createDemoPack.isPending || demoBusy}
            confirmLabel="Generate"
          />
        </DialogContent>
      </Dialog>

      <Dialog open={alphaOpen} onOpenChange={setAlphaOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Publish jobs and people</DialogTitle>
            <DialogDescription>
              Adds jobs and people for rubric work. They stay off recruiter, employer, and candidate
              screens. The sample-job list above does not change. Real testers still sign themselves
              up.
            </DialogDescription>
          </DialogHeader>
          <div className="flex items-center gap-2">
            <Checkbox
              id="alpha-replace"
              checked={alphaReplace}
              onCheckedChange={(value) => setAlphaReplace(value === true)}
              disabled={alphaBusy}
            />
            <Label htmlFor="alpha-replace" className="font-normal">
              Replace the jobs and people already in the live product
            </Label>
          </div>
          {alphaBusy ? (
            <DataBenchJobPanel
              kind="alpha_pack"
              status={packJob?.status || 'queued'}
              progress={packJob?.progress}
              error={packJob?.error}
            />
          ) : null}
          <PackDialogFooter
            onCancel={() => setAlphaOpen(false)}
            cancelDisabled={alphaBusy}
            onConfirm={() => {
              void (async () => {
                try {
                  const job = await createAlphaPack.mutateAsync({
                    name: 'Live product walkthrough',
                    replace: alphaReplace,
                    async_job: true,
                  })
                  if (job.id && job.id !== 'sync') {
                    setPackKind('alpha')
                    setPackJobId(job.id)
                    toast.message('Publishing jobs and people — this may take a few minutes')
                    return
                  }
                  toast.success('Jobs and people are in the live product')
                } catch (err) {
                  toast.error(
                    err instanceof Error ? err.message : 'Could not publish jobs and people',
                  )
                }
              })()
            }}
            confirmDisabled={createAlphaPack.isPending || alphaBusy}
            confirmBusy={createAlphaPack.isPending || alphaBusy}
            confirmLabel="Publish"
          />
        </DialogContent>
      </Dialog>

      <SampleJobArchiveDialog
        open={Boolean(archiveTarget)}
        onOpenChange={(open) => {
          if (!open) setArchiveTarget(null)
        }}
        sampleName={archiveTarget?.name || 'this sample job'}
        isPending={archiveScenario.isPending}
        onConfirm={() => void handleArchive()}
      />
    </PageContainer>
  )
}
